import React, { useState, useEffect, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Fab, Tooltip, Fade } from '@mui/material';
import { WhatsApp } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// Rotas onde o botão WhatsApp NÃO deve aparecer
// Use pathname.includes() - ex: '/chat' esconde em /projects/:id/chat
const HIDDEN_PATHS = [
  '/register',                 // Cadastro (inclui register-landing, register-profile, etc)
  '/lp',                       // Landings de campanha internacional (/lp/funnel/ca, etc)
  '/lead',                     // Landing de captura de leads
  '/creating-account',         // Fluxo de criação de conta
  '/creating-account-checkout',
  '/select-plan',              // Seleção de plano / checkout
  '/chat',                     // Página de chat (já tem interface de conversa)
  '/multi-clients',            // Multi-clientes: só o chat de agência (FAB)
  '/auth/callback',
  '/oauth/callback',
  '/callback',
  '/reset-password',
];

// Animacao de pulso para chamar atencao
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

const WhatsAppFab = styled(Fab, {
  shouldForwardProp: (prop) => prop !== '$show',
})(({ theme, $show }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  backgroundColor: '#25D366',
  color: 'white',
  zIndex: 1000,
  animation: $show ? `${pulse} 2s infinite` : 'none',
  '&:hover': {
    backgroundColor: '#128C7E',
    transform: 'scale(1.1)',
    animation: 'none', // Para a animacao no hover
  },
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
  ...(theme?.breakpoints?.down && {
    [theme.breakpoints.down('sm')]: {
      bottom: 16,
      right: 16,
      width: 48,
      height: 48,
    },
  }),
}));

// Componente wrapper para evitar problemas com refs no Tooltip
const FabWithRef = forwardRef((props, ref) => (
  <WhatsAppFab ref={ref} {...props} />
));

FabWithRef.displayName = 'FabWithRef';

const isPathHidden = (pathname) => {
  return HIDDEN_PATHS.some((path) => pathname.includes(path));
};

const WhatsAppFloat = () => {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Marca como montado apos o primeiro render
    setMounted(true);
    
    // Mostra o botao apos 3 segundos para nao ser intrusivo
    const timer = setTimeout(() => {
      setShow(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Ola Kaiky. preciso de ajuda com a GIO');
    const phoneNumber = '5511918741145';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Nao renderiza nada ate estar montado (evita problemas de hidratacao)
  if (!mounted) {
    return null;
  }

  // Nao exibe em paginas especificas
  if (isPathHidden(location.pathname)) {
    return null;
  }

  // Usa Fade em vez de Zoom para evitar problemas de insertBefore
  // O Fade eh mais estavel com o DOM do React
  return (
    <Fade in={show} timeout={500}>
      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
        <Tooltip 
          title="Precisa de ajuda? Fale conosco no WhatsApp!" 
          arrow 
          placement="left"
          disableInteractive
        >
          <FabWithRef
            size="medium"
            onClick={handleWhatsAppClick}
            aria-label="WhatsApp Support"
            $show={show}
          >
            <WhatsApp />
          </FabWithRef>
        </Tooltip>
      </div>
    </Fade>
  );
};

export default WhatsAppFloat;
